import { attributeOptionMatch, type GeneratedNodeAttribute, type NodeAttributeValue } from "@keydials/api-client";

/**
 * A List attribute (KEY-481) offers one answer from a list the author wrote out. This resolves what the
 * picker should show for one field on one node — kept pure, and out of the panel, for the same reason
 * {@link userAttributeState} is: the interesting rules are all about which values survive, and none of them
 * need a DOM to be checked.
 *
 * Three things end up in the list, in this order:
 *
 * 1. **The author's options**, exactly as authored. Always offered, always first.
 * 2. **The current value, when it is no longer one of them.** Validation happens on the way IN, never on
 *    the way out — so a value stored before the author edited the list still opens and still displays,
 *    flagged rather than blanked. Blanking it would be silent data loss in a field nobody touched.
 * 3. **Values already used elsewhere on this dial**, for an OPEN list only. That is the whole point of an
 *    open list: the second person answering "Failure cause" should be able to reuse the first person's
 *    words instead of inventing a seventh spelling. They come from `InstanceDetail.nodeAttributeValues`,
 *    which the app has already loaded — no new endpoint, no new storage, and nothing from outside this
 *    dial, so no other tenant's answers can appear here.
 *
 * A closed list gets none of (3): an answer nobody authored is exactly what it exists to refuse, and
 * offering one the server would then reject would be a picker that lies.
 */
export interface ListAttributeState {
  /** Every answer the picker offers, deduped case-insensitively, in the order described above. */
  choices: string[];
  /** The current value, trimmed; "" when the field is unset. */
  current: string;
  /** A value is set but is no longer one of the author's options — kept and flagged, never blanked. */
  offList: boolean;
  /** Whether an answer of the person's own is accepted (and so whether "Add another…" is offered). */
  allowUserValues: boolean;
}

/**
 * Every value recorded elsewhere on this dial for the same attribute key — the raw material for an open
 * list's suggestions. Derived at render time from what the instance detail already carries, so nothing is
 * stored and nothing crosses a dial boundary.
 */
export function attributeValuesInUse(
  values: readonly NodeAttributeValue[] | null | undefined,
  attributeKey: string,
): string[] {
  return (values ?? [])
    .filter((v) => v.attributeKey === attributeKey && v.value.trim().length > 0)
    .map((v) => v.value.trim());
}

/** Resolves what a List field should show and offer. `usedElsewhere` is ignored on a closed list. */
export function listAttributeState(
  decl: Pick<GeneratedNodeAttribute, "options" | "allowUserValues">,
  value: string,
  usedElsewhere: readonly string[] = [],
): ListAttributeState {
  const authored = (decl.options ?? []).map((o) => o.trim()).filter((o) => o.length > 0);
  const allowUserValues = decl.allowUserValues === true;
  const current = value.trim();
  // Canonical matching, exactly as the server's: "north west" IS the author's "North West", so a value
  // that only differs in case is not off-list and must not be offered a second time.
  const offList = current.length > 0 && attributeOptionMatch(authored, current) === null;

  const choices: string[] = [];
  const seen = new Set<string>();
  const offer = (option: string) => {
    const folded = option.toLowerCase();
    if (!option || seen.has(folded)) return;
    seen.add(folded);
    choices.push(option);
  };

  authored.forEach(offer);
  if (offList) offer(current);
  if (allowUserValues) usedElsewhere.map((v) => v.trim()).forEach(offer);

  return { choices, current, offList, allowUserValues };
}
